import type { ReviewImpactProfile } from '../shared/desktop-api';

type FieldIdentity = { id: string; name: string };
type ReviewPolicyType = {
  id: string;
  name: string;
  status: string;
  fields: readonly (FieldIdentity & {
    kind: string;
    reviewBelow?: number | null;
    reviewBelowSource?: string | null;
  })[];
};

/** Configuration counts, never a sum of document/field histogram populations. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function fieldReviewPolicyScope(types: readonly ReviewPolicyType[]) {
  let inherited = 0;
  let unavailable = 0;
  const overrides: {
    key: string;
    documentType: string;
    field: string;
    sensitivity: number;
    automatic: boolean;
  }[] = [];
  for (const type of types) {
    if (type.status === 'archived') continue;
    for (const field of type.fields) {
      if (field.kind === 'line-items' || field.kind === 'document-type') continue;
      if (field.reviewBelow == null) {
        inherited++;
        continue;
      }
      if (!Number.isFinite(field.reviewBelow) || field.reviewBelow < 0 || field.reviewBelow > 1) {
        unavailable++;
        continue;
      }
      overrides.push({
        key: `${type.id}:${field.id}`,
        documentType: type.name,
        field: field.name,
        sensitivity: Math.round(field.reviewBelow * 100),
        automatic: field.reviewBelowSource === 'auto',
      });
    }
  }
  const summary =
    unavailable > 0
      ? 'Some field settings could not be read; their effect is unavailable.'
      : inherited === 0 && overrides.length === 0
        ? 'Starting threshold for new fields.'
        : inherited === 0 && overrides.length > 0
          ? 'Every current field has its own threshold. This slider sets the starting point for fields without an individual setting.'
          : `${inherited} ${inherited === 1 ? 'field uses' : 'fields use'} this job-wide threshold${overrides.length ? `; ${overrides.length} ${overrides.length === 1 ? 'uses an individual setting' : 'use individual settings'}` : ''}.`;
  return { inherited, overrides, summary };
}
export type FieldReviewImpact =
  | { kind: 'unavailable' }
  | { kind: 'no-readings' }
  | { kind: 'measured'; histogram: readonly number[]; readings: number };

export const FIELD_REVIEW_IMPACT_SCOPE =
  'Confidence-only estimate for this field in the latest saved batch, using whole-percent score buckets. Blank, confirmed and operator-entered values are excluded. Required values and other validation problems have separate review rules.';

/** The native profile excludes blank, verified and operator-entered values.
 * Its job-wide document count is not this field's measured population. */
export function selectFieldReviewImpact(
  profile: ReviewImpactProfile | null,
  field: FieldIdentity,
  configuredFields: readonly FieldIdentity[],
): FieldReviewImpact {
  if (!profile) return { kind: 'unavailable' };
  const exact = profile.fields.filter((entry) => entry.fieldId === field.id);
  if (exact.length > 1) return { kind: 'unavailable' };
  let entry = exact[0];
  if (!entry) {
    const name = field.name.trim().toLocaleLowerCase();
    const legacy = profile.fields.filter(
      (candidate) => !candidate.fieldId && candidate.fieldName.trim().toLocaleLowerCase() === name,
    );
    // An explicit different identity is never a fallback. Old name-only rows
    // cannot distinguish two types that each configure a field named Total.
    if (
      legacy.length > 1 ||
      (legacy.length === 1 &&
        configuredFields.filter((candidate) => candidate.name.trim().toLocaleLowerCase() === name)
          .length !== 1)
    )
      return { kind: 'unavailable' };
    entry = legacy[0];
  }
  if (!entry) return { kind: 'no-readings' };
  const histogram = entry.documents;
  if (
    histogram.length !== 101 ||
    histogram.some((count) => !Number.isSafeInteger(count) || count < 0)
  )
    return { kind: 'unavailable' };
  const readings = histogram.reduce((sum, count) => sum + count, 0);
  if (!Number.isSafeInteger(readings)) return { kind: 'unavailable' };
  return readings > 0 ? { kind: 'measured', histogram, readings } : { kind: 'no-readings' };
}

export function fieldReviewImpactText(
  impact: FieldReviewImpact,
  internalThreshold: number,
): string {
  if (impact.kind === 'unavailable') return 'Confidence preview unavailable for this field.';
  if (impact.kind === 'no-readings') return 'No unconfirmed readings to estimate yet.';
  const cutoff = Math.max(0, Math.min(101, Math.round(internalThreshold * 100)));
  const asking = impact.histogram.slice(0, cutoff).reduce((sum, count) => sum + count, 0);
  const summary = `${asking.toLocaleString()} of ${impact.readings.toLocaleString()} readings would need review.`;
  const least = impact.histogram.findIndex((count) => count > 0);
  return asking === 0 ? `${summary} Lowest score: ${least} / 100.` : summary;
}
